import React, { useState, useEffect, useRef } from 'react';
import { ref, set, onValue, off, remove } from 'firebase/database';
import { database } from '../firebase/config';
import './VideoCall.css';

function VideoCall({ tripId, user, participants, isHost, onClose }) {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState(new Map());
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isListenOnly, setIsListenOnly] = useState(false); // 1-way mode: receive only
  const [participantStates, setParticipantStates] = useState(new Map());
  const [hostMuteAll, setHostMuteAll] = useState(false);
  const [hostCamerasOff, setHostCamerasOff] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [retryingMedia, setRetryingMedia] = useState(false);
  
  const localVideoRef = useRef(null);
  const peerConnectionsRef = useRef(new Map());
  const localStreamRef = useRef(null);
  const signalingRefs = useRef([]);

  // Helper function to get user-friendly error message
  const getMediaErrorMessage = (error) => {
    if (!error) return 'Unknown error';
    
    const errorName = error.name || '';
    const errorMessage = error.message || '';
    
    if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
      return 'Camera/microphone permission was denied. Please allow access in your browser settings and try again.';
    } else if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
      return 'No camera or microphone found. Please connect a device and try again.';
    } else if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
      return 'Camera/microphone is being used by another application. Please close other apps and try again.';
    } else if (errorName === 'OverconstrainedError' || errorName === 'ConstraintNotSatisfiedError') {
      return 'Your device does not support the required camera/microphone settings.';
    } else if (errorName === 'TypeError') {
      return 'Media access is not supported in this browser. Please use a modern browser like Chrome, Firefox, or Edge.';
    } else {
      return `Unable to access camera/microphone: ${errorMessage || errorName}. Please check your device permissions.`;
    }
  };

  // Initialize local media stream
  useEffect(() => {
    // Skip if we already have a stream (toggleListenOnly handles its own media)
    if (localStreamRef.current) {
      return;
    }

    const initializeMedia = async (retryCount = 0) => {
      try {
        setMediaError(null);
        setRetryingMedia(retryCount > 0);
        
        // Check if getUserMedia is available
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('getUserMedia is not supported in this browser');
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        
        localStreamRef.current = stream;
        setLocalStream(stream);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        setMediaError(null);
        setRetryingMedia(false);
      } catch (error) {
        console.error('Error accessing media devices:', error);
        const errorMsg = getMediaErrorMessage(error);
        setMediaError(errorMsg);
        setRetryingMedia(false);
        
        // Auto-retry once for permission errors (user might have just granted permission)
        if (retryCount === 0 && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')) {
          console.log('🔄 Will retry media access in 2 seconds...');
          setTimeout(() => {
            if (!isListenOnly && !localStreamRef.current) {
              initializeMedia(1);
            }
          }, 2000);
        } else {
          // Show error after retry fails or for other errors
          console.error('❌ Media access failed:', errorMsg);
        }
      }
    };

    if (!isListenOnly) {
      initializeMedia();
    } else {
      setMediaError(null);
    }

    return () => {
      // Cleanup only runs on unmount or when isListenOnly changes
      // If entering listen-only mode, toggleListenOnly handles cleanup
      // If exiting listen-only mode, we want to keep the stream
      // Only cleanup on component unmount
      // (We check if component is unmounting by checking if isListenOnly is still the same)
    };
  }, [isListenOnly]);

  // Update video element when localStream changes
  useEffect(() => {
    if (localStream && localVideoRef.current && !isListenOnly) {
      // Only update if the srcObject is different
      if (localVideoRef.current.srcObject !== localStream) {
        localVideoRef.current.srcObject = localStream;
        // Force video to reload and play
        localVideoRef.current.load();
        localVideoRef.current.play().catch(err => {
          console.error('Error playing video after stream update:', err);
        });
      }
    }
  }, [localStream, isListenOnly]);

  // WebRTC configuration
  const rtcConfiguration = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ],
  };

  // Create peer connection
  const createPeerConnection = (userId, listenOnly = false) => {
    const pc = new RTCPeerConnection(rtcConfiguration);

    // Add local stream tracks (only if not in listen-only mode)
    if (localStreamRef.current && !listenOnly) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    // Handle remote stream
    pc.ontrack = (event) => {
      const [remoteStream] = event.streams;
      setRemoteStreams(prev => {
        const newMap = new Map(prev);
        newMap.set(userId, remoteStream);
        return newMap;
      });
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const signalingRef = ref(database, `roadTrips/${tripId}/signaling/${user.uid}/candidates/${userId}`);
        set(signalingRef, {
          candidate: event.candidate.candidate,
          sdpMLineIndex: event.candidate.sdpMLineIndex,
          sdpMid: event.candidate.sdpMid,
          timestamp: Date.now(),
        });
      }
    };

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log(`Connection state with ${userId}:`, pc.connectionState);
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        // Try to reconnect
        setTimeout(() => {
          if (pc.connectionState !== 'connected' && pc.connectionState !== 'connecting') {
            initiateConnection(userId);
          }
        }, 1000);
      }
    };

    peerConnectionsRef.current.set(userId, pc);
    return pc;
  };

  // Initiate connection with another user
  const initiateConnection = async (targetUserId) => {
    if (targetUserId === user.uid) return;
    if (peerConnectionsRef.current.has(targetUserId)) return;

    const pc = createPeerConnection(targetUserId, isListenOnly);

    try {
      // Create offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Send offer
      const offerRef = ref(database, `roadTrips/${tripId}/signaling/${targetUserId}/offers/${user.uid}`);
      await set(offerRef, {
        type: 'offer',
        sdp: offer.sdp,
        timestamp: Date.now(),
      });
    } catch (error) {
      console.error('Error creating offer:', error);
    }
  };

  // Handle incoming offer
  const handleOffer = async (fromUserId, offerData) => {
    let pc = peerConnectionsRef.current.get(fromUserId);
    if (!pc) {
      pc = createPeerConnection(fromUserId, isListenOnly);
    }

    try {
      const offer = new RTCSessionDescription({
        type: offerData.type || 'offer',
        sdp: offerData.sdp,
      });
      await pc.setRemoteDescription(offer);
      
      // Create answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      // Send answer
      const answerRef = ref(database, `roadTrips/${tripId}/signaling/${fromUserId}/answers/${user.uid}`);
      await set(answerRef, {
        type: 'answer',
        sdp: answer.sdp,
        timestamp: Date.now(),
      });
    } catch (error) {
      console.error('Error handling offer:', error);
    }
  };

  // Handle incoming answer
  const handleAnswer = async (fromUserId, answerData) => {
    const pc = peerConnectionsRef.current.get(fromUserId);
    if (pc) {
      try {
        const answer = new RTCSessionDescription({
          type: answerData.type || 'answer',
          sdp: answerData.sdp,
        });
        await pc.setRemoteDescription(answer);
      } catch (error) {
        console.error('Error handling answer:', error);
      }
    }
  };

  // Handle ICE candidates
  const handleIceCandidate = async (fromUserId, candidate) => {
    const pc = peerConnectionsRef.current.get(fromUserId);
    if (pc) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (error) {
        console.error('Error adding ICE candidate:', error);
      }
    }
  };

  // Set up signaling listeners
  useEffect(() => {
    if (!tripId || !user) return;

    const participantIds = participants?.map(p => p.uid) || [];
    
    // Listen for offers (from other participants to me)
    const offersRef = ref(database, `roadTrips/${tripId}/signaling/${user.uid}/offers`);
    const offersListener = onValue(offersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        Object.keys(data).forEach(fromUserId => {
          if (fromUserId !== user.uid) {
            handleOffer(fromUserId, data[fromUserId]);
            // Clean up after handling
            const offerPathRef = ref(database, `roadTrips/${tripId}/signaling/${user.uid}/offers/${fromUserId}`);
            remove(offerPathRef);
          }
        });
      }
    });
    signalingRefs.current.push(offersRef);

    // Listen for answers (from other participants to me)
    const answersRef = ref(database, `roadTrips/${tripId}/signaling/${user.uid}/answers`);
    const answersListener = onValue(answersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        Object.keys(data).forEach(fromUserId => {
          if (fromUserId !== user.uid) {
            handleAnswer(fromUserId, data[fromUserId]);
            // Clean up after handling
            const answerPathRef = ref(database, `roadTrips/${tripId}/signaling/${user.uid}/answers/${fromUserId}`);
            remove(answerPathRef);
          }
        });
      }
    });
    signalingRefs.current.push(answersRef);

    // Listen for ICE candidates (from other participants to me)
    const candidatesRef = ref(database, `roadTrips/${tripId}/signaling/${user.uid}/candidates`);
    const candidatesListener = onValue(candidatesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        Object.keys(data).forEach(fromUserId => {
          if (fromUserId !== user.uid && data[fromUserId]) {
            handleIceCandidate(fromUserId, {
              candidate: data[fromUserId].candidate,
              sdpMLineIndex: data[fromUserId].sdpMLineIndex,
              sdpMid: data[fromUserId].sdpMid,
            });
            // Clean up after handling
            const candidatePathRef = ref(database, `roadTrips/${tripId}/signaling/${user.uid}/candidates/${fromUserId}`);
            remove(candidatePathRef);
          }
        });
      }
    });
    signalingRefs.current.push(candidatesRef);

    // Initiate connections with all participants
    participantIds.forEach(participantId => {
      if (participantId !== user.uid) {
        setTimeout(() => initiateConnection(participantId), 1000);
      }
    });

    return () => {
      signalingRefs.current.forEach(ref => off(ref));
      signalingRefs.current = [];
    };
  }, [tripId, user, participants]);

  // Toggle mute
  const toggleMute = () => {
    if (localStreamRef.current && !isListenOnly) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = isMuted;
      });
      setIsMuted(!isMuted);
    }
  };

  // Toggle video
  const toggleVideo = () => {
    if (localStreamRef.current && !isListenOnly) {
      localStreamRef.current.getVideoTracks().forEach(track => {
        track.enabled = isVideoOff;
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  // Host: Mute all participants
  const handleMuteAll = async () => {
    if (!isHost || !database) return;
    
    const newMuteState = !hostMuteAll;
    setHostMuteAll(newMuteState);
    
    // Write command to Firebase
    const commandRef = ref(database, `roadTrips/${tripId}/hostCommands/muteAll`);
    await set(commandRef, {
      enabled: newMuteState,
      timestamp: Date.now(),
    });
  };

  // Host: Turn off all cameras
  const handleCamerasOff = async () => {
    if (!isHost || !database) return;
    
    const newCameraState = !hostCamerasOff;
    setHostCamerasOff(newCameraState);
    
    // Write command to Firebase
    const commandRef = ref(database, `roadTrips/${tripId}/hostCommands/camerasOff`);
    await set(commandRef, {
      enabled: newCameraState,
      timestamp: Date.now(),
    });
  };

  // Toggle listen-only mode (1-way: receive only)
  const toggleListenOnly = async () => {
    const newListenOnly = !isListenOnly;

    if (newListenOnly) {
      // Entering listen-only mode: stop sending tracks
      setIsListenOnly(true); // Set state first
      
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        localStreamRef.current = null;
        setLocalStream(null);
      }
      
      // Remove tracks from all peer connections
      peerConnectionsRef.current.forEach((pc, userId) => {
        const senders = pc.getSenders();
        senders.forEach(sender => {
          if (sender.track) {
            pc.removeTrack(sender);
          }
        });
      });
    } else {
      // Exiting listen-only mode: get media first, then update state
      try {
        setMediaError(null);
        setRetryingMedia(true);
        
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        
        // Only update state if we successfully got media
        setIsListenOnly(false);
        setRetryingMedia(false);
        
        localStreamRef.current = stream;
        setLocalStream(stream);
        
        // Update video element and ensure it plays
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          // Force video to reload and play
          localVideoRef.current.load();
          localVideoRef.current.play().catch(err => {
            console.error('Error playing video after stream restore:', err);
          });
        }

        // Add tracks to all existing peer connections
        peerConnectionsRef.current.forEach((pc) => {
          stream.getTracks().forEach(track => {
            // Check if track already exists to avoid duplicates
            const existingSenders = pc.getSenders();
            const hasTrack = existingSenders.some(sender => sender.track && sender.track.id === track.id);
            if (!hasTrack) {
              pc.addTrack(track, stream);
            }
          });
        });

        // Re-initiate connections with new tracks
        const participantIds = participants?.map(p => p.uid) || [];
        participantIds.forEach(participantId => {
          if (participantId !== user.uid) {
            // Recreate offer with new tracks
            setTimeout(() => {
              const pc = peerConnectionsRef.current.get(participantId);
              if (pc) {
                // Create new offer with updated tracks
                pc.createOffer().then(offer => {
                  pc.setLocalDescription(offer);
                  const offerRef = ref(database, `roadTrips/${tripId}/signaling/${participantId}/offers/${user.uid}`);
                  set(offerRef, {
                    type: 'offer',
                    sdp: offer.sdp,
                    timestamp: Date.now(),
                  });
                }).catch(err => {
                  console.error('Error creating offer after stream restore:', err);
                });
              } else {
                initiateConnection(participantId);
              }
            }, 500);
          }
        });
      } catch (error) {
        console.error('Error accessing media devices:', error);
        const errorMsg = getMediaErrorMessage(error);
        setMediaError(errorMsg);
        setRetryingMedia(false);
        // Keep isListenOnly as true since we failed to get media
        // Don't revert - just show the error and stay in listen-only mode
      }
    }
  };

  // Listen for host commands (mute all, cameras off)
  useEffect(() => {
    if (!tripId || !database) return;

    // Listen for mute all command
    const muteAllRef = ref(database, `roadTrips/${tripId}/hostCommands/muteAll`);
    const muteAllListener = onValue(muteAllRef, (snapshot) => {
      const data = snapshot.val();
      if (data && data.enabled && localStreamRef.current && !isListenOnly) {
        // Host commanded mute all - mute this participant
        localStreamRef.current.getAudioTracks().forEach(track => {
          track.enabled = false;
        });
        setIsMuted(true);
      } else if (data && !data.enabled && localStreamRef.current && !isListenOnly) {
        // Host released mute all - restore previous state (but keep muted if user had muted themselves)
        // Only unmute if the user hadn't manually muted
        if (!isMuted) {
          localStreamRef.current.getAudioTracks().forEach(track => {
            track.enabled = true;
          });
        }
      }
    });

    // Listen for cameras off command
    const camerasOffRef = ref(database, `roadTrips/${tripId}/hostCommands/camerasOff`);
    const camerasOffListener = onValue(camerasOffRef, (snapshot) => {
      const data = snapshot.val();
      if (data && data.enabled && localStreamRef.current && !isListenOnly) {
        // Host commanded cameras off - turn off this participant's camera
        localStreamRef.current.getVideoTracks().forEach(track => {
          track.enabled = false;
        });
        setIsVideoOff(true);
      } else if (data && !data.enabled && localStreamRef.current && !isListenOnly) {
        // Host released cameras off - restore previous state
        if (!isVideoOff) {
          localStreamRef.current.getVideoTracks().forEach(track => {
            track.enabled = true;
          });
        }
      }
    });

    return () => {
      off(muteAllRef);
      off(camerasOffRef);
    };
  }, [tripId, database, isListenOnly, isMuted, isVideoOff]);

  // Update participant states (mute/video status)
  useEffect(() => {
    const stateRef = ref(database, `roadTrips/${tripId}/participantStates/${user.uid}`);
    set(stateRef, {
      isMuted,
      isVideoOff,
      isListenOnly,
      userName: user.displayName || user.email.split('@')[0],
      timestamp: Date.now(),
    });

    return () => {
      remove(stateRef);
    };
  }, [tripId, user.uid, isMuted, isVideoOff, isListenOnly, database]);

  // Listen to other participants' states
  useEffect(() => {
    if (!tripId) return;

    const participantIds = participants?.map(p => p.uid) || [];
    const stateListeners = [];

    participantIds.forEach(participantId => {
      if (participantId === user.uid) return;

      const stateRef = ref(database, `roadTrips/${tripId}/participantStates/${participantId}`);
      const listener = onValue(stateRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setParticipantStates(prev => {
            const newMap = new Map(prev);
            newMap.set(participantId, data);
            return newMap;
          });
        }
      });
      stateListeners.push({ ref: stateRef, listener });
    });

    return () => {
      stateListeners.forEach(({ ref }) => off(ref));
    };
  }, [tripId, participants, user.uid]);

  return (
    <div className="video-call-overlay">
      <div className="video-call-container">
        <div className="video-call-header">
          <h3>📹 JookCam Meeting Room</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="video-grid">
          {/* Media error message */}
          {mediaError && !isListenOnly && (
            <div className="video-item local media-error">
              <div className="media-error-content">
                <div className="media-error-icon">⚠️</div>
                <div className="media-error-title">Media Access Error</div>
                <div className="media-error-message">{mediaError}</div>
                <button
                  className="media-retry-btn"
                  onClick={() => {
                    setMediaError(null);
                    if (!isListenOnly) {
                      const initializeMedia = async () => {
                        try {
                          const stream = await navigator.mediaDevices.getUserMedia({
                            video: true,
                            audio: true,
                          });
                          localStreamRef.current = stream;
                          setLocalStream(stream);
                          if (localVideoRef.current) {
                            localVideoRef.current.srcObject = stream;
                          }
                          setMediaError(null);
                        } catch (error) {
                          const errorMsg = getMediaErrorMessage(error);
                          setMediaError(errorMsg);
                        }
                      };
                      initializeMedia();
                    }
                  }}
                >
                  🔄 Retry
                </button>
                <button
                  className="media-listen-only-btn"
                  onClick={() => setIsListenOnly(true)}
                >
                  👂 Use Listen-Only Mode
                </button>
              </div>
            </div>
          )}
          
          {/* Retrying indicator */}
          {retryingMedia && !mediaError && (
            <div className="video-item local">
              <div className="media-retrying">
                <div className="retrying-spinner">⏳</div>
                <div className="retrying-text">Requesting camera/microphone access...</div>
              </div>
            </div>
          )}
          
          {/* Local video */}
          {!isListenOnly && !mediaError && !retryingMedia && localStream && (
            <div className="video-item local">
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className="video-element"
                key={localStream?.id || 'local-video'} // Force re-render when stream changes
              />
              <div className="video-overlay">
                <span className="video-name">
                  {user.displayName || user.email.split('@')[0]} (You)
                </span>
                {isMuted && <span className="mute-indicator">🔇</span>}
                {isVideoOff && <span className="video-off-indicator">📷</span>}
              </div>
            </div>
          )}
          {isListenOnly && (
            <div className="video-item local listen-only">
              <div className="listen-only-placeholder">
                <div className="listen-only-icon">👂</div>
                <div className="listen-only-text">Listen Only Mode</div>
                <div className="listen-only-name">
                  {user.displayName || user.email.split('@')[0]}
                </div>
              </div>
            </div>
          )}

          {/* Remote videos */}
          {Array.from(remoteStreams.entries()).map(([userId, stream]) => {
            const state = participantStates.get(userId);
            const participant = participants?.find(p => p.uid === userId);
            const userName = participant?.name || participant?.email?.split('@')[0] || 'Unknown';

            return (
              <RemoteVideo
                key={userId}
                stream={stream}
                userId={userId}
                userName={userName}
                isMuted={state?.isMuted || false}
                isVideoOff={state?.isVideoOff || false}
                isListenOnly={state?.isListenOnly || false}
              />
            );
          })}
        </div>

        {/* Controls */}
        <div className="video-controls">
          <button
            className={`control-btn ${isListenOnly ? 'listen-only-active' : ''}`}
            onClick={toggleListenOnly}
            title={isListenOnly ? 'Exit listen-only mode (2-way)' : 'Enter listen-only mode (1-way)'}
          >
            {isListenOnly ? '👂' : '🔊'}
            <span className="control-label">{isListenOnly ? 'Listen Only' : '2-Way'}</span>
          </button>
          <button
            className={`control-btn ${isMuted ? 'muted' : ''}`}
            onClick={toggleMute}
            disabled={isListenOnly}
            title={isListenOnly ? 'Disabled in listen-only mode' : (isMuted ? 'Unmute' : 'Mute')}
          >
            {isMuted ? '🔇' : '🎤'}
          </button>
          <button
            className={`control-btn ${isVideoOff ? 'video-off' : ''}`}
            onClick={toggleVideo}
            disabled={isListenOnly}
            title={isListenOnly ? 'Disabled in listen-only mode' : (isVideoOff ? 'Turn on camera' : 'Turn off camera')}
          >
            {isVideoOff ? '📷' : '📹'}
          </button>
          
          {/* Host-only controls */}
          {isHost && (
            <>
              <div className="host-controls-divider"></div>
              <button
                className={`control-btn host-control ${hostMuteAll ? 'active' : ''}`}
                onClick={handleMuteAll}
                title={hostMuteAll ? 'Unmute all participants' : 'Mute all participants'}
              >
                {hostMuteAll ? '🔊' : '🔇'}
                <span className="control-label">Mute All</span>
              </button>
              <button
                className={`control-btn host-control ${hostCamerasOff ? 'active' : ''}`}
                onClick={handleCamerasOff}
                title={hostCamerasOff ? 'Turn on all cameras' : 'Turn off all cameras'}
              >
                {hostCamerasOff ? '📹' : '📷'}
                <span className="control-label">Cameras Off</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Remote video component
function RemoteVideo({ stream, userId, userName, isMuted, isVideoOff }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  return (
    <div className="video-item remote">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="video-element"
      />
      <div className="video-overlay">
        <span className="video-name">{userName}</span>
        {isMuted && <span className="mute-indicator">🔇</span>}
        {isVideoOff && <span className="video-off-indicator">📷</span>}
      </div>
    </div>
  );
}

export default VideoCall;

